// AdminTransferLog — audit trail of all HQ→team movements. Also hosts
// the "Add manual transfer" panel for admins.

import React, { useState } from "react";
import { ArrowRight, ChevronRight, Download, Plus } from "lucide-react";
import { NAVY, TEAL, useInv } from "./constants";
import { SEED_CATALOGUE, SEED_HQ_STOCK, SEED_REQUESTS, SEED_TEAMS, catItem } from "./seed";
import { SectionHeader, StatusPill, TeamBadge } from "./ui";

export function AdminTransferLog() {
  const {
    requests: REQUESTS = SEED_REQUESTS,
    catalogue: CATALOGUE = SEED_CATALOGUE,
    teams: TEAMS = SEED_TEAMS,
    hqStock: HQ = SEED_HQ_STOCK,
    activeTeam,
    createTransfer,
    hasToken,
  } = useInv();
  const decided = REQUESTS.filter((r) => r.status !== "pending" && (!activeTeam || r.team === activeTeam));
  const [open, setOpen] = useState(false);

  // Admin-only inline "add transfer" form state.
  const [addOpen, setAddOpen] = useState(false);
  const [tfTeam, setTfTeam] = useState("");
  const [tfItem, setTfItem] = useState("");
  const [tfQty, setTfQty] = useState(1);
  const [tfMovedBy, setTfMovedBy] = useState("");
  const [tfReason, setTfReason] = useState("");
  const [tfBusy, setTfBusy] = useState(false);
  const [tfErr, setTfErr] = useState("");
  const outreachTeams = TEAMS.filter((t) => t.key !== "hq");
  const resetForm = () => {
    setTfTeam(outreachTeams[0]?.key || "");
    setTfItem(CATALOGUE[0]?.id || "");
    setTfQty(1);
    setTfMovedBy("");
    setTfReason("");
    setTfErr("");
  };
  const openAdd = () => {
    resetForm();
    setAddOpen(true);
    setOpen(true); // auto-expand the table so admin can see the new row land
  };
  const saveTransfer = async () => {
    if (!tfTeam) { setTfErr("Choose a team."); return; }
    if (!tfItem) { setTfErr("Choose an item."); return; }
    const qty = Number(tfQty);
    if (!qty || qty < 1) { setTfErr("Qty must be at least 1."); return; }
    const onHand = HQ[tfItem] ?? 0;
    if (qty > onHand) { setTfErr(`Only ${onHand} in HQ — reduce qty.`); return; }
    setTfBusy(true); setTfErr("");
    try {
      await createTransfer({ team: tfTeam, item: tfItem, qty, reason: tfReason, movedBy: tfMovedBy });
      setAddOpen(false);
    } catch (e) {
      setTfErr(e.message || "Could not log transfer");
    } finally { setTfBusy(false); }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
      <SectionHeader
        eyebrow="Admin · Audit"
        title="Transfer log"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {hasToken && (
              <button
                onClick={() => addOpen ? setAddOpen(false) : openAdd()}
                data-testid="add-transfer-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-white shadow-sm"
                style={{ background: TEAL }}>
                <Plus className="h-3.5 w-3.5" /> {addOpen ? "Cancel" : "Add transfer"}
              </button>
            )}
            <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-white shadow-sm" style={{ background: NAVY }}>
              <Download className="h-3.5 w-3.5" /> Download PDF
            </button>
          </div>
        }
      />

      {hasToken && addOpen && (
        <div className="mt-3 rounded-lg border-2 p-4" style={{ borderColor: TEAL, background: `${TEAL}08` }} data-testid="add-transfer-panel">
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold mb-3" style={{ color: NAVY }}>New manual transfer · HQ → team</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Team *</span>
              <select value={tfTeam} onChange={(e) => setTfTeam(e.target.value)} data-testid="transfer-team-select" className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm bg-white">
                <option value="">— select —</option>
                {outreachTeams.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Item *</span>
              <select value={tfItem} onChange={(e) => setTfItem(e.target.value)} data-testid="transfer-item-select" className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm bg-white">
                <option value="">— select —</option>
                {CATALOGUE.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} · HQ has {HQ[c.id] ?? 0}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Qty *</span>
              <input type="number" min="1" value={tfQty} onChange={(e) => setTfQty(e.target.value)} data-testid="transfer-qty-input" className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm text-right tabular-nums" />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Moved by</span>
              <input value={tfMovedBy} onChange={(e) => setTfMovedBy(e.target.value)} placeholder="Name (optional)" data-testid="transfer-movedby-input" className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Reason / note</span>
              <input value={tfReason} onChange={(e) => setTfReason(e.target.value)} placeholder="e.g. Off-cycle top-up" data-testid="transfer-reason-input" className="mt-0.5 h-9 w-full rounded-md border border-slate-300 px-2 text-sm" />
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2 items-center flex-wrap">
            {tfErr && <p className="text-xs text-red-600 mr-auto" data-testid="transfer-form-err">{tfErr}</p>}
            <button onClick={() => setAddOpen(false)} className="h-8 px-3 rounded-md text-xs font-semibold border border-slate-300 text-slate-600">Cancel</button>
            <button onClick={saveTransfer} disabled={tfBusy} data-testid="save-transfer-btn" className="h-8 px-4 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: TEAL }}>
              {tfBusy ? "Logging…" : "Log transfer"}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="toggle-transfer-log-btn"
        className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition mt-2">
        <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Movements</span>
        <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: NAVY }}>
          {decided.length}
          <ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />
        </span>
      </button>
      {open && (
      <div className="overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6 mt-2">
        <table className="w-full min-w-[640px] text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2.5 pr-4 font-semibold">When</th>
              <th className="py-2.5 pr-4 font-semibold">Movement</th>
              <th className="py-2.5 pr-4 font-semibold">Item</th>
              <th className="py-2.5 pr-4 font-semibold text-right">Qty</th>
              <th className="py-2.5 pr-4 font-semibold">Status</th>
              <th className="py-2.5 pr-4 font-semibold">Authorized by</th>
            </tr>
          </thead>
          <tbody>
            {decided.map((r) => {
              const it = catItem(r.item, CATALOGUE) || { name: r.item };
              const whenDate = (r.movedAt || r.at || "").split("·")[0].trim();
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-[#F0F5F4]/60 transition">
                  <td className="py-3 pr-4 text-slate-700 tabular-nums">{whenDate}</td>
                  <td className="py-3 pr-4">
                    <span className="inline-flex items-center gap-1 text-xs">
                      <span className="inline-flex items-center h-6 px-2 rounded-md font-semibold" style={{ background: `${NAVY}12`, color: NAVY }}>HQ</span>
                      <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                      <TeamBadge team={r.team} />
                    </span>
                  </td>
                  <td className="py-3 pr-4 font-semibold" style={{ color: NAVY }}>{it.name}</td>
                  <td className="py-3 pr-4 text-right tabular-nums font-semibold">{r.qty}</td>
                  <td className="py-3 pr-4"><StatusPill status={r.status} /></td>
                  <td className="py-3 pr-4 text-slate-700">HQ</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}
